/* ============================================================
 * MOBILE FILTER CHROME — Leads + Daily Requests (2026-10-16, revised)
 * ------------------------------------------------------------
 * Owner report: on a 390px phone the top block of Leads / Daily Request took
 * ~70% of the screen. First attempt (a "Filters" bottom sheet) was rejected:
 * everything stays ON the page, tidy and compact.
 *
 * This file is PRESENTATION ONLY. It owns no filter state and no rule: the pages
 * keep every control they already render (same JSX nodes, same setters, same
 * counts) and hand them in. Nothing here decides what a filter does, what a chip
 * counts, or who may see a control — a control the page did not render is simply
 * not in the list it passes (role gating stays the page's).
 *   MobileActions      the small ＋ button + its menu (Add / Import / Export …).
 *                      Mounted INTO the header row through a portal by the page.
 *   FilterGrid         the compact select grid: 3 columns for 6/3 cells, 2×2 for
 *                      4, and a last cell that spans the row when the count would
 *                      leave a hole (5) — "balanced" for every role's cell count.
 *   ToggleRow          small pill toggles in one wrapping line; ON = filled accent.
 *   ActiveFilterStrip  removable pills + "Clear all", only when something is set.
 * Desktop (>768px) never mounts any of this — the pages return their original
 * tree there. No overflow:auto/hidden wrapper is introduced (the scroll-ancestors
 * gate). verify-mobile-filter-chrome.js renders it.
 * ============================================================ */
import React, { Children, useEffect, useLayoutEffect, useRef, useState } from "react";
import { UserPlus, Upload, Download, RotateCcw, Plus } from "lucide-react";

var INK = "#1E293B", MUTED = "#64748B", LINE = "#E2E8F0", ACCENT = "#E8A838", DANGER = "#EF4444", INFO = "#3B82F6";

// ---- status chips: exactly three balanced rows --------------------------------------------------------------
// Free-flow wrapping cannot do this: 12 chips with real counts ("Rot. Stopped 4467") broke 4/3/3/2 or left one orphan on a
// fourth row at 390px at every font size that is still readable. So the chips are MEASURED and PARTITIONED:
// contiguous rows, the widest row as narrow as it can be, each row then justified to the full width.
// partitionRows(widths, gap, R) -> { max, cuts } where cuts are the start indexes of rows 2..R. Exported for the gate.
export function partitionRows(widths, gap, R) {
  var n = widths.length;
  R = Math.max(1, Math.min(R, n));
  var pre = [0];
  widths.forEach(function (w) { pre.push(pre[pre.length - 1] + w); });
  var span = function (i, j) { return pre[j] - pre[i] + gap * (j - i - 1); };   // chips i..j-1 on one row
  var dp = [], cut = [], r, j, k;
  for (r = 0; r <= R; r++) { dp.push(new Array(n + 1).fill(Infinity)); cut.push(new Array(n + 1).fill(0)); }
  dp[0][0] = 0;
  for (r = 1; r <= R; r++) for (j = r; j <= n; j++) for (k = r - 1; k < j; k++) {
    var v = Math.max(dp[r - 1][k], span(k, j));
    if (v < dp[r][j]) { dp[r][j] = v; cut[r][j] = k; }
  }
  var cuts = [];
  for (r = R, j = n; r > 1; r--) { j = cut[r][j]; cuts.unshift(j); }
  return { max: dp[R][n], cuts: cuts };
}
// The row count: `want` (3) unless three genuinely cannot fit the width W — then the fewest extra rows that do.
// Never truncates, never scrolls; the last resort is one chip per row.
export function chooseRows(widths, W, gap, want) {
  var R = Math.min(want, widths.length), res = partitionRows(widths, gap, R);
  while (res.max > W && R < widths.length) { R++; res = partitionRows(widths, gap, R); }
  return { rows: R, max: res.max, cuts: res.cuts };
}
function textOf(el) {
  if (el == null || typeof el === "boolean") return "";
  if (typeof el === "string" || typeof el === "number") return String(el);
  if (Array.isArray(el)) return el.map(textOf).join("");
  return el.props ? textOf(el.props.children) : "";
}
// Replace text inside an element tree (used for the one permitted abbreviation).
function mapText(node, pairs) {
  if (typeof node === "string") return pairs.reduce(function (t, x) { return t.split(x[0]).join(x[1]); }, node);
  if (Array.isArray(node)) return node.map(function (n) { return mapText(n, pairs); });
  if (node && node.props && node.props.children != null) return React.cloneElement(node, undefined, mapText(node.props.children, pairs));
  return node;
}
// Labels stay EXACTLY as on desktop. The abbreviated variant is used only when it buys a row the full labels cannot:
// returns { abbr, res } — abbr is true only when the abbreviated widths need FEWER rows than the full ones AND the full
// ones need more rows than `want`.
export function pickLabelVariant(fullWidths, abbrWidths, W, gap, want) {
  var full = chooseRows(fullWidths, W, gap, want);
  if (!abbrWidths || full.rows <= want) return { abbr: false, res: full };
  var ab = chooseRows(abbrWidths, W, gap, want);
  return ab.rows < full.rows ? { abbr: true, res: ab } : { abbr: false, res: full };
}
// props: rows (default 3), fill (default false: chips HUG their text, rows start at the left; true justifies each row),
// abbreviate [[from,to]] (optional, see pickLabelVariant), children (the page's own buttons).
// Starts as plain wrapping, then lays out from measurements; uses more rows ONLY when `rows` cannot fit the width W —
// never truncates, never scrolls. Line breaks are chosen to keep rows balanced (no lone chip on a last row).
export function ChipRows(p) {
  var items = Children.toArray(p.children).filter(Boolean);
  var want = p.rows || 3, gap = 6, fill = !!p.fill, abbrevPairs = p.abbreviate || null;
  var boxRef = useRef(null), measRef = useRef(null), measAbbrRef = useRef(null);
  var st = useState(null), layout = st[0], setLayout = st[1];
  var measure = function () {
    var box = boxRef.current, m = measRef.current;
    if (!box || !m || !items.length) return;
    var W = box.clientWidth;
    if (!W || m.children.length !== items.length) return;
    var widths = function (el) { return [].map.call(el.children, function (c) { return Math.ceil(c.getBoundingClientRect().width); }); };
    var wf = widths(m), wa = (abbrevPairs && measAbbrRef.current && measAbbrRef.current.children.length === items.length) ? widths(measAbbrRef.current) : null;
    var pick = pickLabelVariant(wf, wa, W, gap, want), ws = pick.abbr ? wa : wf;
    var key = ws.join(",") + "@" + W + (pick.abbr ? "a" : "");
    setLayout(function (l) { return l && l.key === key ? l : { key: key, cuts: pick.res.cuts, ws: ws, abbr: pick.abbr }; });
  };
  useLayoutEffect(measure);   // every render; the functional setState bails out when nothing changed
  useEffect(function () {
    var ro = (typeof ResizeObserver !== "undefined" && boxRef.current) ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(boxRef.current);
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    return function () { if (ro) ro.disconnect(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var shown = (layout && layout.abbr) ? items.map(function (el) { return mapText(el, abbrevPairs); }) : items;
  var rows = null;
  if (layout && layout.ws.length === items.length) {
    var starts = [0].concat(layout.cuts), ends = layout.cuts.concat([items.length]);
    rows = starts.map(function (a, ri) {
      return <div key={ri} className="crm-m-chiprow" data-m-chiprow={ri} style={{ display: "flex", gap: gap, alignItems: "flex-start" }}>
        {shown.slice(a, ends[ri]).map(function (el, i) {
          return React.cloneElement(el, { style: Object.assign({}, el.props.style, fill ? { flex: layout.ws[a + i] + " 1 0px", minWidth: 0 } : { flex: "0 0 auto" }) });
        })}
      </div>;
    });
  }
  var measurer = function (ref, arr, prefix) {
    return <div ref={ref} aria-hidden="true" className="crm-m-chiprow" style={{ position: "fixed", left: -10000, top: 0, visibility: "hidden", pointerEvents: "none", display: "flex", gap: gap, width: "max-content" }}>
      {arr.map(function (el, i) { return React.cloneElement(el, { key: prefix + i, tabIndex: -1, style: Object.assign({}, el.props.style, { flex: "0 0 auto" }) }); })}
    </div>;
  };
  return <div ref={boxRef} data-m-chiprows="1" style={{ display: "flex", flexDirection: "column", gap: gap }}>
    {measurer(measRef, items, "m")}
    {abbrevPairs && measurer(measAbbrRef, items.map(function (el) { return mapText(el, abbrevPairs); }), "a")}
    {rows || <div className="crm-m-chiprow" style={{ display: "flex", flexWrap: "wrap", gap: gap, alignItems: "flex-start" }}>{items}</div>}
  </div>;
}

// ---- the floating action button (FAB) + speed-dial ------------------------------------------------------------
// props: open, onToggle, onClose, hidden, children — the page's OWN action nodes (its buttons and its hidden file <input>).
// The buttons' handlers are reused as they are (label read off the button, click forwards to the page's onClick); a role
// with no buttons gets no FAB. 52px amber ＋ bottom-right, stacked 12px above the floating search button (same column,
// 24px + 46px + 12px from the bottom, safe-area aware); open: a dark ✕ and the actions fan UPWARD as labelled pills, each
// with a 40px tinted round icon button, the page behind dimmed to 45%. Hidden (CSS, body:has overlay) under modals/drawers.
var FAB_LOOKS = [
  [/reassign/i, { bg: "#EDE9FE", fg: "#6D28D9", Icon: RotateCcw, rank: 1 }],
  [/import/i, { bg: "#DBEAFE", fg: "#1D4ED8", Icon: Upload, rank: 1 }],
  [/export/i, { bg: "#DCFCE7", fg: "#15803D", Icon: Download, rank: 2 }],
  [/add|new/i, { bg: "#FEF3C7", fg: "#B45309", Icon: UserPlus, rank: 0 }]
];
function fabLook(label) {
  for (var i = 0; i < FAB_LOOKS.length; i++) if (FAB_LOOKS[i][0].test(label)) return FAB_LOOKS[i][1];
  return { bg: "#F1F5F9", fg: "#475569", Icon: Plus, rank: 3 };
}
export function fabActions(children) {
  var list = Children.toArray(children).filter(function (el) { return el && el.props && el.type !== "input" && typeof el.props.onClick === "function"; }).map(function (el, i) {
    var raw = textOf(el.props.children).replace(/\s*\(\d+\)\s*$/, "").trim();
    return { el: el, label: raw === "New Request" ? "Add Request" : raw, look: fabLook(raw), i: i };
  });
  // primary action nearest the thumb: Add first, then Import / Bulk Reassign, then Export (stable within a rank)
  var seen = {};
  list = list.filter(function (a) { if (seen[a.label]) return false; seen[a.label] = 1; return true; });   // one pill per label (first wins)
  return list.sort(function (a, b) { return a.look.rank - b.look.rank || a.i - b.i; });
}
export function MobileFab(p) {
  var all = Children.toArray(p.children).filter(Boolean);
  var inputs = all.filter(function (el) { return el.type === "input"; });
  var acts = fabActions(all);
  if (!acts.length) return null;
  if (p.hidden) return <>{inputs}</>;
  var stack = { position: "fixed", right: "calc(21px + env(safe-area-inset-right, 0px))", bottom: "calc(82px + env(safe-area-inset-bottom, 0px))", zIndex: 120,
    display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 };
  return <div data-m-fab="1">
    {inputs}
    {p.open && <div data-m-scrim="1" onClick={p.onClose} style={{ position: "fixed", inset: 0, zIndex: 119, background: "rgba(15,23,42,0.45)" }}/>}
    <div style={stack}>
      {p.open && acts.slice().reverse().map(function (a, i) {
        var Icon = a.look.Icon;
        var go = function (e) { p.onClose(); a.el.props.onClick(e); };
        return <div key={a.label} data-m-fabrow="1" style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 6, animation: "crm-fab-in 0.16s ease both", animationDelay: (i * 0.03) + "s" }}>
          <button type="button" onClick={go} data-m-fabpill="1" style={{ height: 36, padding: "0 14px", borderRadius: 18, border: "none", background: "#fff", color: INK, fontSize: 13, fontWeight: 700, fontFamily: "inherit", whiteSpace: "nowrap", boxShadow: "0 4px 14px rgba(15,23,42,0.22)", cursor: "pointer" }}>{a.label}</button>
          <button type="button" onClick={go} aria-label={a.label} data-m-fabicon="1" style={{ width: 40, height: 40, borderRadius: 20, border: "none", background: a.look.bg, color: a.look.fg, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(15,23,42,0.22)", cursor: "pointer", padding: 0 }}><Icon size={18} strokeWidth={2.2}/></button>
        </div>;
      })}
      <button type="button" onClick={p.onToggle} aria-label={p.open ? "Close actions" : "Actions"} aria-expanded={!!p.open} data-m-fabbtn="1"
        style={{ width: 52, height: 52, borderRadius: 26, border: "none", background: p.open ? INK : ACCENT, color: "#fff", fontSize: p.open ? 22 : 30, lineHeight: "50px", fontWeight: 400, cursor: "pointer", padding: 0, boxShadow: "0 6px 18px rgba(15,23,42,0.28)", transition: "background 0.15s" }}>{p.open ? "✕" : "＋"}</button>
    </div>
  </div>;
}

// ---- select mode (Bulk Reassign on a phone) -------------------------------------------------------------------
// Phone cards have no checkboxes, so Bulk Reassign enters a SELECT MODE: each card shows a checkbox top-left, tapping a card
// toggles it, and a sticky bar shows "N selected · All · Reassign · Cancel". All state is the page's own (selected2 /
// showBulk — the desktop selection and the existing modal); this file only draws it.
// MobileSelectBox: the round checkbox drawn on a card (pointer-events none: the card's own tap toggles).
export function MobileSelectBox(p) {
  return <span data-m-selbox="1" data-on={p.on ? "1" : "0"} aria-hidden="true" style={{ position: "absolute", top: 14, left: 14, width: 24, height: 24, borderRadius: 12, boxSizing: "border-box",
    border: "2px solid " + (p.on ? ACCENT : "#CBD5E1"), background: p.on ? ACCENT : "#fff", color: "#fff", fontSize: 14, fontWeight: 800, lineHeight: "20px", textAlign: "center", pointerEvents: "none" }}>{p.on ? "✓" : ""}</span>;
}
// props: count, allSelected, onToggleAll, onReassign, onCancel, noun ("leads" | "requests")
export function MobileSelectBar(p) {
  var n = p.count || 0;
  return <div data-m-selbar="1" style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 130, background: "#fff", borderTop: "1px solid " + LINE, boxShadow: "0 -6px 20px rgba(15,23,42,0.14)",
    padding: "10px 12px calc(10px + env(safe-area-inset-bottom, 0px))", display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ flex: "1 1 auto", minWidth: 0, fontSize: 13, fontWeight: 800, color: INK, whiteSpace: "nowrap" }}>{n} selected</div>
    <button type="button" data-m-selall="1" onClick={p.onToggleAll} style={{ height: 38, padding: "0 12px", borderRadius: 19, border: "1px solid " + LINE, background: p.allSelected ? "#FFF8E8" : "#fff", color: INK, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap", minHeight: 0 }}>{p.allSelected ? "☑ All" : "☐ All"}</button>
    <button type="button" data-m-selreassign="1" disabled={n === 0} onClick={p.onReassign} style={{ height: 38, padding: "0 16px", borderRadius: 19, border: "none", background: n === 0 ? "#E2E8F0" : INFO, color: n === 0 ? "#94A3B8" : "#fff", fontSize: 13, fontWeight: 800, fontFamily: "inherit", cursor: n === 0 ? "not-allowed" : "pointer", whiteSpace: "nowrap", minHeight: 0 }}>Reassign</button>
    <button type="button" data-m-selcancel="1" onClick={p.onCancel} style={{ height: 38, padding: "0 8px", border: "none", background: "transparent", color: MUTED, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", minHeight: 0 }}>Cancel</button>
  </div>;
}

// Column count and last-cell span for n cells: 6 -> 3x2, 4 -> 2x2, 5 -> 3 + a last cell spanning 2, 3 -> 3, 1 -> 1.
// A last cell that would leave a hole on a 3-wide row spans the rest of it (7 cells: 3+3+1 wide).
function shapeOf(n) {
  var cols = n === 4 ? 2 : Math.min(n, 3);
  return { cols: cols, lastSpan: cols === 3 ? (n % 3 === 2 ? 2 : (n % 3 === 1 && n > 3 ? 3 : 1)) : 1 };
}

// props: children — the grid's cells. Falsy cells (a control the role does not have) are dropped.
export function FilterGrid(p) {
  var cells = Children.toArray(p.children).filter(Boolean);
  var n = cells.length;
  if (!n) return null;
  var sh = shapeOf(n), cols = sh.cols, lastSpan = sh.lastSpan;
  return <div data-m-grid="1" data-cols={cols} style={{ display: "grid", gridTemplateColumns: "repeat(" + cols + ", minmax(0, 1fr))", gap: 6 }}>
    {cells.map(function (c, i) {
      var span = i === n - 1 ? lastSpan : 1;
      return <div key={i} data-mf="1" style={{ minWidth: 0, gridColumn: span > 1 ? "span " + span : undefined }}>{c}</div>;
    })}
  </div>;
}

// props: items [{node, on}] (node falsy => the role does not have it), children (the trailing cell, e.g. Sort pill).
// Five same-height pills hugging their text: ONE line when they fit, otherwise two balanced lines (never one giant item);
// ON = filled accent, keyed on data-on.
export function ToggleRow(p) {
  var items = (p.items || []).filter(function (x) { return x && x.node; });
  var extra = Children.toArray(p.children).filter(Boolean);
  if (!items.length && !extra.length) return null;
  var cells = items.map(function (x, i) {
    return <span key={"t" + i} data-mt="1" data-on={x.on ? "1" : "0"} style={{ display: "flex" }}>{x.node}</span>;
  }).concat(extra.map(function (c, i) {
    return <div key={"x" + i} data-mf="1" data-msort="1" style={{ display: "flex" }}>{c}</div>;
  }));
  return <div data-m-toggles="1"><ChipRows rows={1}>{cells}</ChipRows></div>;
}

// A removable pill. The × is its own ≥28px target.
function Pill(p) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 2, maxWidth: 190, height: 26, padding: "0 2px 0 9px",
    borderRadius: 13, background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1D4ED8", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>
    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</span>
    <button type="button" aria-label={"Remove filter: " + p.label} onClick={p.onRemove}
      style={{ width: 28, height: 24, border: "none", background: "transparent", color: "#1D4ED8", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0 }}>✕</button>
  </span>;
}

// props: pills [{key,label,onRemove}], onClearAll. Renders nothing at the default state.
export function ActiveFilterStrip(p) {
  var pills = p.pills || [];
  if (!pills.length) return null;
  return <div data-m-active="1" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5 }}>
    {pills.map(function (x) { return <Pill key={x.key} label={x.label} onRemove={x.onRemove}/>; })}
    <button type="button" onClick={p.onClearAll} data-m-clearall="1"
      style={{ marginInlineStart: 2, height: 26, padding: "0 6px", border: "none", background: "transparent", color: DANGER, fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Clear all</button>
  </div>;
}

export var MOBILE_TOKENS = { ink: INK, muted: MUTED, line: LINE, accent: ACCENT };
